import type { ReactNode } from "react";

import { PageHead, Shell } from "@ui/react";

/**
 * The chrome for every screen outside the workspace.
 *
 * It is the SAME shell the workspace uses, in its `auth` variant: brand,
 * glow, footer — no links, no account cluster. Sign-in is the first screen most
 * contractors ever see and it used to carry no Shoku mark at all, no legal
 * links and no way to tell it was the same product as the marketing page.
 *
 * The step rail lives inside the card, above the form. These six screens have
 * no navigation, so a contractor part-way through onboarding has no other way
 * to tell how much is left — the mobile app hides that too and it is a
 * measurable drop-off point. The rail exists to say "two more things".
 */

const STEPS = ["Account", "Email", "Profile", "Payouts"] as const;

export type OnboardingStep = 1 | 2 | 3 | 4;

function StepRail({ step }: { step: OnboardingStep }) {
  return (
    <div>
      <p className="sr-only">{`Step ${step} of ${STEPS.length}: ${STEPS[step - 1]}`}</p>
      <ol className="cluster cluster--nowrap cluster--tight step-rail">
        {STEPS.map((label, index) => {
          const position = index + 1;
          const reached = position <= step;
          return (
            <li
              key={label}
              className="step-rail__step"
              {...(position === step ? { "aria-current": "step" as const } : {})}
            >
              <span
                aria-hidden="true"
                className={reached ? "step-rail__bar is-reached" : "step-rail__bar"}
              />
              {/* The bar alone would encode progress in colour only, so every
                  step keeps its name and the current one is also weighted. */}
              <span
                className={
                  position === step ? "step-rail__label label-sm" : "step-rail__label label-sm dim"
                }
              >
                {label}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export function OnboardingLayout({
  eyebrow,
  title,
  lead,
  step,
  children,
  footer,
}: {
  eyebrow: string;
  title: string;
  lead?: ReactNode;
  step?: OnboardingStep;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <Shell variant="auth" brandHref="/login">
      {/* The eyebrow used to be `p.label-sm.dim` — the dimmest text on the
          first screen of the product. It is the reference's orange dotted
          `.eyebrow` now, and the lead is `.lead`, so the page head reads
          identically here and on the marketing site. */}
      <PageHead eyebrow={eyebrow} title={title} lead={lead} />

      <div className="card card--dense">
        <div className="stack stack--loose">
          {step ? <StepRail step={step} /> : null}
          {children}
        </div>
      </div>

      {footer ? <div className="auth-note">{footer}</div> : null}
    </Shell>
  );
}
